import React, { createContext, useCallback, useContext, useMemo, useReducer, useRef, useState } from 'react'
import PropTypes from 'prop-types'

const SmartEndpointWorkspaceContext = createContext({})

/**
 * Registry for imperative actions that components can register.
 * Allows cross-component communication without lifting all state.
 */
function useActionRegistry () {
  const handlers = useRef({})

  const register = useCallback((name, handler) => {
    handlers.current[name] = handler
    return () => { delete handlers.current[name] }
  }, [])

  const dispatch = useCallback((name, payload) => {
    const handler = handlers.current[name]
    if (handler) handler(payload)
  }, [])

  return { register, dispatch }
}

export const useSmartEndpointWorkspace = () => useContext(SmartEndpointWorkspaceContext)

export const DEFAULT_ENDPOINT_CONFIG = {
  maxReadRequests: 50,
  maxWriteRequests: 5,
  timeoutMs: 25000,
  allowedPolicies: ['danger_zone']
}

function SmartEndpointWorkspaceProvider ({ children }) {
  const [mode, setMode] = useState('query')
  const [activeMiddleTab, setActiveMiddleTab] = useState('tests')

  const [endpointConfig, setEndpointConfig] = useState(DEFAULT_ENDPOINT_CONFIG)
  const [testsVersion, bumpTestsVersion] = useReducer(v => v + 1, 0)
  const actions = useActionRegistry()
  const testsRef = useRef([])

  const value = useMemo(() => ({
    mode,
    setMode,
    activeMiddleTab,
    setActiveMiddleTab,
    endpointConfig,
    setEndpointConfig,
    registerAction: actions.register,
    dispatchAction: actions.dispatch,
    testsRef,
    testsVersion,
    bumpTestsVersion
  }), [
    mode, activeMiddleTab,
    endpointConfig, testsVersion,
    actions.register, actions.dispatch
  ])

  return (
    <SmartEndpointWorkspaceContext.Provider value={value}>
      {children}
    </SmartEndpointWorkspaceContext.Provider>
  )
}

SmartEndpointWorkspaceProvider.propTypes = {
  children: PropTypes.node
}

export default SmartEndpointWorkspaceProvider
